// components/OutcomeSheet.tsx — K4's own sheet (c5-outcome.md: "a sheet, not
// a new screen"). Opened from either spread's Outcomes pane — creation
// (outcomeId omitted) or the full panel (outcomeId given): every field, a
// reading, the client confirmation, and moving state (the generic
// /records/:id/transition door — c5's own route list names no dedicated
// transition endpoint for the outcome).
//
// Same rise-over-the-app construction as MoreSheet/FindSheet: fixed/scrim/
// box, Escape closes. Machine voice throughout; failures show the server's
// own sentence (ApiError.message) rather than a caption of our own.
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  ApiError,
  createOutcome,
  updateOutcome,
  recordOutcomeReading,
  confirmOutcomeWithClient,
  getRecord,
  getRecordTypeSchema,
  transitionRecord,
  type RecordRow,
  type RecordTypeSchema,
} from '../lib/api';
import './OutcomeSheet.css';

export interface OutcomeSheetProps {
  tenantId: string;
  accountId: string;
  engagementId: string | null;
  /** null = creating a new outcome. */
  outcomeId: string | null;
  onClose: () => void;
  onSaved: () => void;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

const REALISED_WORD: Record<string, string> = {
  not_yet: 'Not yet', partly: 'Partly', yes: 'Yes', no: 'No',
};

export function OutcomeSheet({ tenantId, accountId, engagementId, outcomeId, onClose, onSaved }: OutcomeSheetProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [record, setRecord] = useState<RecordRow | null>(null);
  const [schema, setSchema] = useState<RecordTypeSchema | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  // Create-mode fields.
  const [measure, setMeasure] = useState('');
  const [unit, setUnit] = useState('');
  const [baselineValue, setBaselineValue] = useState('');
  const [baselineAsOf, setBaselineAsOf] = useState(today());
  const [targetValue, setTargetValue] = useState('');
  const [targetBy, setTargetBy] = useState('');
  const [method, setMethod] = useState('');
  const [responsible, setResponsible] = useState('');
  const [reviewOn, setReviewOn] = useState('');

  // Reading mini-form.
  const [reading, setReading] = useState(false);
  const [readingValue, setReadingValue] = useState('');
  const [readingDate, setReadingDate] = useState(today());
  const [readingNote, setReadingNote] = useState('');

  // Confirm mini-form.
  const [confirming, setConfirming] = useState(false);
  const [confirmDate, setConfirmDate] = useState(today());
  const [confirmBy, setConfirmBy] = useState('');

  // Move-to.
  const [moving, setMoving] = useState(false);
  const [moveTo, setMoveTo] = useState('');

  function refresh() {
    if (!outcomeId) return Promise.resolve();
    return Promise.all([getRecord(tenantId, outcomeId), getRecordTypeSchema(tenantId, 'outcome')])
      .then(([r, s]) => {
        setRecord(r);
        setSchema(s);
      })
      .catch((err: unknown) => setLoadError(err instanceof ApiError ? err.message : 'this outcome could not be read'));
  }

  useEffect(() => {
    void refresh();
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, outcomeId]);

  async function save() {
    if (!measure.trim()) {
      setNote('a measure is required — what the work is meant to change');
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      await createOutcome(tenantId, {
        account_id: accountId,
        engagement_id: engagementId,
        measure: measure.trim(),
        unit: unit.trim() || null,
        baseline_value: baselineValue ? Number(baselineValue) : null,
        baseline_as_of: baselineAsOf || null,
        target_value: targetValue ? Number(targetValue) : null,
        target_by: targetBy || null,
        method: method.trim() || null,
        responsible: responsible.trim() || null,
        review_on: reviewOn || null,
      });
      onSaved();
      onClose();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'this outcome was refused');
    } finally {
      setBusy(false);
    }
  }

  async function submitReading() {
    if (!record || !readingValue) return;
    setBusy(true);
    setNote(null);
    try {
      const updated = await recordOutcomeReading(tenantId, record.id, {
        value: Number(readingValue),
        measuredOn: readingDate,
        note: readingNote.trim() || undefined,
      });
      setRecord(updated);
      setReading(false);
      setReadingValue('');
      setReadingNote('');
      onSaved();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'that reading was refused');
    } finally {
      setBusy(false);
    }
  }

  async function submitConfirm() {
    if (!record || !confirmBy.trim()) return;
    setBusy(true);
    setNote(null);
    try {
      const updated = await confirmOutcomeWithClient(tenantId, record.id, { on: confirmDate, by: confirmBy.trim() });
      setRecord(updated);
      setConfirming(false);
      setConfirmBy('');
      onSaved();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'that confirmation was refused');
    } finally {
      setBusy(false);
    }
  }

  async function move() {
    if (!record || !moveTo) return;
    setBusy(true);
    setNote(null);
    try {
      const updated = await transitionRecord(tenantId, record.id, moveTo);
      setRecord(updated);
      setMoving(false);
      onSaved();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'that move was refused');
    } finally {
      setBusy(false);
    }
  }

  const states = schema?.lifecycle?.states ?? [];
  const nextStates = record ? schema?.lifecycle?.transitions[record.lifecycle_state ?? ''] ?? [] : [];

  return (
    <div class="oc-sheet" data-testid="outcome-sheet">
      <button type="button" class="oc-sheet__scrim" aria-label="Close" onClick={onClose} />
      <div class="oc-sheet__box" ref={boxRef} role="dialog" aria-label="Outcome">
        <div class="oc-sheet__grip" aria-hidden="true" />

        {!outcomeId ? (
          <>
            <h2 class="oc-sheet__title">New outcome</h2>
            <label class="oc-sheet__field">
              <span>Measure</span>
              <input value={measure} disabled={busy} onInput={(e) => setMeasure((e.target as HTMLInputElement).value)} />
            </label>
            <label class="oc-sheet__field">
              <span>Unit</span>
              <input value={unit} disabled={busy} onInput={(e) => setUnit((e.target as HTMLInputElement).value)} />
            </label>
            <div class="oc-sheet__row2">
              <label class="oc-sheet__field">
                <span>Baseline</span>
                <input type="number" value={baselineValue} disabled={busy} onInput={(e) => setBaselineValue((e.target as HTMLInputElement).value)} />
              </label>
              <label class="oc-sheet__field">
                <span>As of</span>
                <input type="date" value={baselineAsOf} disabled={busy} onInput={(e) => setBaselineAsOf((e.target as HTMLInputElement).value)} />
              </label>
            </div>
            <div class="oc-sheet__row2">
              <label class="oc-sheet__field">
                <span>Target</span>
                <input type="number" value={targetValue} disabled={busy} onInput={(e) => setTargetValue((e.target as HTMLInputElement).value)} />
              </label>
              <label class="oc-sheet__field">
                <span>Target by</span>
                <input type="date" value={targetBy} disabled={busy} onInput={(e) => setTargetBy((e.target as HTMLInputElement).value)} />
              </label>
            </div>
            <label class="oc-sheet__field">
              <span>Method</span>
              <input value={method} disabled={busy} onInput={(e) => setMethod((e.target as HTMLInputElement).value)} />
            </label>
            <label class="oc-sheet__field">
              <span>Responsible</span>
              <input value={responsible} disabled={busy} onInput={(e) => setResponsible((e.target as HTMLInputElement).value)} />
            </label>
            <label class="oc-sheet__field">
              <span>Review on</span>
              <input type="date" value={reviewOn} disabled={busy} onInput={(e) => setReviewOn((e.target as HTMLInputElement).value)} />
            </label>
            {note ? <p class="oc-sheet__note">{note}</p> : null}
            <div class="oc-sheet__acts">
              <button type="button" class="oc-sheet__btn" disabled={busy} onClick={() => void save()}>Save</button>
              <button type="button" class="oc-sheet__btn oc-sheet__btn--ghost" disabled={busy} onClick={onClose}>Never mind</button>
            </div>
          </>
        ) : loadError ? (
          <p class="oc-sheet__note">{loadError}</p>
        ) : !record ? (
          <p class="oc-sheet__say">One moment…</p>
        ) : (
          <>
            <h2 class="oc-sheet__title">{String(record.fields.measure ?? record.display_name)}</h2>
            <div class="oc-sheet__facts">
              <div><span>State</span><b>{record.lifecycle_state ?? '—'}</b></div>
              <div><span>Baseline → target</span><b>{String(record.fields.baseline_value ?? '—')} → {String(record.fields.target_value ?? '—')} {String(record.fields.unit ?? '')}</b></div>
              <div><span>Current</span><b>{String(record.fields.current_value ?? '—')}</b></div>
              <div><span>Measured on</span><b>{String(record.fields.measured_on ?? '—')}</b></div>
              <div><span>Review on</span><b>{String(record.fields.review_on ?? '—')}</b></div>
              <div><span>Realised</span><b>{REALISED_WORD[String(record.fields.realised ?? 'not_yet')] ?? '—'}</b></div>
              <div><span>Client confirmed</span><b>{record.fields.client_confirmed_on ? `${record.fields.client_confirmed_on} · ${record.fields.client_confirmed_by ?? ''}` : 'Not yet'}</b></div>
            </div>

            {nextStates.length > 0 ? (
              !moving ? (
                <div class="oc-sheet__acts">
                  <button type="button" class="oc-sheet__btn oc-sheet__btn--ghost" onClick={() => { setMoveTo(nextStates[0] ?? ''); setMoving(true); }}>Move to…</button>
                </div>
              ) : (
                <div class="oc-sheet__acts">
                  <select value={moveTo} disabled={busy} onChange={(e) => setMoveTo((e.target as HTMLSelectElement).value)}>
                    {nextStates.map((s) => <option value={s} key={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                  <button type="button" class="oc-sheet__btn" disabled={busy} onClick={() => void move()}>Move</button>
                  <button type="button" class="oc-sheet__btn oc-sheet__btn--ghost" disabled={busy} onClick={() => setMoving(false)}>Never mind</button>
                </div>
              )
            ) : null}

            {!reading ? (
              <div class="oc-sheet__acts">
                <button type="button" class="oc-sheet__btn oc-sheet__btn--ghost" onClick={() => setReading(true)}>Record a reading</button>
              </div>
            ) : (
              <div class="oc-sheet__mini">
                <div class="oc-sheet__row2">
                  <label class="oc-sheet__field"><span>Value</span><input type="number" value={readingValue} disabled={busy} onInput={(e) => setReadingValue((e.target as HTMLInputElement).value)} /></label>
                  <label class="oc-sheet__field"><span>On</span><input type="date" value={readingDate} disabled={busy} onInput={(e) => setReadingDate((e.target as HTMLInputElement).value)} /></label>
                </div>
                <label class="oc-sheet__field"><span>Note (optional)</span><input value={readingNote} disabled={busy} onInput={(e) => setReadingNote((e.target as HTMLInputElement).value)} /></label>
                <div class="oc-sheet__acts">
                  <button type="button" class="oc-sheet__btn" disabled={busy} onClick={() => void submitReading()}>Keep reading</button>
                  <button type="button" class="oc-sheet__btn oc-sheet__btn--ghost" disabled={busy} onClick={() => setReading(false)}>Never mind</button>
                </div>
              </div>
            )}

            {!confirming ? (
              <div class="oc-sheet__acts">
                <button type="button" class="oc-sheet__btn oc-sheet__btn--ghost" onClick={() => setConfirming(true)}>Confirm with the client</button>
              </div>
            ) : (
              <div class="oc-sheet__mini">
                <div class="oc-sheet__row2">
                  <label class="oc-sheet__field"><span>On</span><input type="date" value={confirmDate} disabled={busy} onInput={(e) => setConfirmDate((e.target as HTMLInputElement).value)} /></label>
                  <label class="oc-sheet__field"><span>By</span><input value={confirmBy} disabled={busy} onInput={(e) => setConfirmBy((e.target as HTMLInputElement).value)} /></label>
                </div>
                <div class="oc-sheet__acts">
                  <button type="button" class="oc-sheet__btn" disabled={busy} onClick={() => void submitConfirm()}>Confirm</button>
                  <button type="button" class="oc-sheet__btn oc-sheet__btn--ghost" disabled={busy} onClick={() => setConfirming(false)}>Never mind</button>
                </div>
              </div>
            )}

            {note ? <p class="oc-sheet__note">{note}</p> : null}
            <div class="oc-sheet__acts">
              <button type="button" class="oc-sheet__btn oc-sheet__btn--ghost" onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
